// PartnerDetail › Profile (2026-10-22): the legacy entity profile — one table, a label on the left and its setting on
// the right, one "Update this partner" button — instead of a panel per setting. Holds what the partner is (Entity ID —
// four digits, QOR's system admins change it, the API refuses one already taken —, type,
// processor, currency, status), KYC (required: cardholders apply; not required: staff issue cards — instant issue), its money limits, what its cardholders can do, and the
// monthly inactivity fee with its delay. Saving sends only the parts that changed, each to its own endpoint. The fee is
// for reference only (2026-10-23): QOR does not charge it; it is sent to the processor, which has the bank approve it
// and charges it itself.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import {
  adminApi,
  fundsApi,
  type ProgramFeatureState,
  type ProgramLimits,
  type ProgramSummary,
  type SettingRow,
  type SettingValue,
} from '../api';
import { useStaff } from '../components/RequireStaff';
import { featureLabel } from '../scope';

const FEE_AMOUNT = 'fees.inactivityMonthly';
const FEE_AFTER = 'fees.inactivityAfterMonths';

type LimitKey = keyof Omit<ProgramLimits, 'currency' | 'isDefault'>;

/** Each limit as a sentence around its field, as the legacy profile wrote its fraud rules. */
const LIMIT_ROWS: { key: LimitKey; label: string; before: string; after: string; optional: boolean }[] = [
  { key: 'maxLoad', label: 'One load', before: 'At most', after: 'per load', optional: false },
  { key: 'dailyCardLoad', label: 'Per card', before: 'At most', after: 'loaded onto a card per day', optional: false },
  {
    key: 'dailyProgramLoad',
    label: 'Whole partner',
    before: 'At most',
    after: "loaded onto this partner's cards per day",
    optional: true,
  },
  {
    key: 'kycCardLimit',
    label: 'Before KYC',
    before: 'A card may hold at most',
    after: 'before KYC',
    optional: true,
  },
  {
    key: 'approvalThreshold',
    label: 'Second approval',
    before: 'Loads above',
    after: 'need a second admin',
    optional: true,
  },
];

interface Fee {
  amount: string;
  afterMonths: string;
}

function feeOf(rows: SettingRow[]): Fee {
  const value = (key: string) => rows.find((r) => r.key === key)?.value ?? null;
  return {
    amount: value(FEE_AMOUNT) === null ? '' : String(value(FEE_AMOUNT)),
    afterMonths: value(FEE_AFTER) === null ? '' : String(value(FEE_AFTER)),
  };
}

/** The settings change that turns `before` into `now`: only the fields that moved. */
function feeChange(before: Fee, now: Fee) {
  const set: Record<string, SettingValue> = {};
  const clear: string[] = [];
  if (now.amount.trim() !== before.amount) {
    if (now.amount.trim() === '') clear.push(FEE_AMOUNT);
    else set[FEE_AMOUNT] = now.amount.trim();
  }
  if (now.afterMonths.trim() !== before.afterMonths) set[FEE_AFTER] = Number(now.afterMonths.trim());
  return Object.keys(set).length + clear.length === 0 ? null : { set, clear };
}

function Row({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="qc-profile-row">
      {htmlFor ? (
        <label className="qc-profile-label" htmlFor={htmlFor}>
          {label}
        </label>
      ) : (
        <div className="qc-profile-label">{label}</div>
      )}
      <div className="qc-profile-value">{children}</div>
    </div>
  );
}

export function PartnerProfilePanel({
  partner,
  features,
  limits,
  after,
}: {
  partner: ProgramSummary;
  features: ProgramFeatureState[];
  limits: ProgramLimits | null;
  after: (ok: boolean, message: string) => void;
}) {
  const settings = useQuery({ queryKey: ['fees-settings', partner.id], queryFn: () => fundsApi.settings(partner.id) });
  if (settings.isPending) return null;
  return (
    <ProfileForm
      key={settings.dataUpdatedAt}
      partner={partner}
      features={features}
      limits={limits}
      feeRows={(settings.data ?? []).filter((r) => r.group === 'fees')}
      after={after}
    />
  );
}

function ProfileForm({
  partner,
  features,
  limits,
  feeRows,
  after,
}: {
  partner: ProgramSummary;
  features: ProgramFeatureState[];
  limits: ProgramLimits | null;
  feeRows: SettingRow[];
  after: (ok: boolean, message: string) => void;
}) {
  const client = useQueryClient();
  const { user } = useStaff();
  const sysadmin = user.role === 'SystemAdmin';
  const canFeatures = sysadmin || user.role === 'ProgramAdmin';
  const shown = features.filter((f) => f.feature !== 'AddMoney');

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [staffIssue, setStaffIssue] = useState(partner.instantIssue);
  const [code, setCode] = useState(partner.code);
  const [enabled, setEnabled] = useState(() => Object.fromEntries(shown.map((f) => [f.feature, f.isEnabled])));
  const [limitForm, setLimitForm] = useState(limits);
  const feeBefore = feeOf(feeRows);
  const [fee, setFee] = useState(feeBefore);
  const feePending = feeRows.filter((r) => r.pending);
  const feeInherited = feeRows.length > 0 && feeRows.every((r) => !r.hasOwn);

  async function save(e: FormEvent) {
    e.preventDefault();
    setProblem(null);
    if (fee.amount.trim() !== '' && !/^\d+$/.test(fee.afterMonths.trim()))
      return setProblem('Enter after how many months without activity the fee starts.');

    const codeChanged = sysadmin && code.trim() !== partner.code;
    if (codeChanged && !/^\d{4}$/.test(code.trim())) return setProblem('The Entity ID is four digits.');

    const featuresChanged = shown.some((f) => enabled[f.feature] !== f.isEnabled);
    const issuingChanged = staffIssue !== partner.instantIssue;
    const limitsChanged =
      limits !== null &&
      limitForm !== null &&
      LIMIT_ROWS.some(({ key }) => (limitForm[key] ?? '') !== (limits[key] ?? ''));
    const feeDiff = sysadmin ? feeChange(feeBefore, fee) : null;
    if (!codeChanged && !featuresChanged && !issuingChanged && !limitsChanged && !feeDiff)
      return after(true, 'Nothing changed.');
    if (
      issuingChanged &&
      staffIssue &&
      !window.confirm(
        `Switch ${partner.displayName} to no KYC? Staff will issue each card with its amount; cardholders are no longer invited or asked to verify their identity.`,
      )
    )
      return;

    setBusy(true);
    const saved: string[] = [];
    const failed: string[] = [];
    if (codeChanged) {
      const r = await adminApi.setProgramCode(partner.id, code.trim());
      if (r.success) saved.push('Entity ID');
      else failed.push(r.error ?? "Couldn't change the Entity ID.");
    }
    if (featuresChanged) {
      const r = await adminApi.setProgramFeatures(
        partner.id,
        shown.map((f) => ({ feature: f.feature, isEnabled: enabled[f.feature] === true })),
      );
      if (r.success) saved.push('cardholder features');
      else failed.push(r.error ?? "Couldn't save the cardholder features.");
    }
    if (issuingChanged) {
      const r = await adminApi.setInstantIssue(partner.id, staffIssue);
      if (r.success) saved.push('KYC');
      else failed.push(r.error ?? "Couldn't change the KYC setting.");
    }
    if (limitsChanged && limitForm) {
      const blank = (v: string | null) => (v === null || v.trim() === '' ? null : v.trim());
      const r = await adminApi.setLimits(partner.id, {
        currency: limitForm.currency,
        maxLoad: limitForm.maxLoad.trim(),
        dailyCardLoad: limitForm.dailyCardLoad.trim(),
        dailyProgramLoad: blank(limitForm.dailyProgramLoad),
        kycCardLimit: blank(limitForm.kycCardLimit),
        approvalThreshold: blank(limitForm.approvalThreshold),
      });
      if (r.success) saved.push('money limits');
      else failed.push(r.error ?? "Couldn't update the money limits.");
    }
    if (feeDiff) {
      const r = await fundsApi.changeSettings(partner.id, feeDiff);
      if (r.ok) saved.push('inactivity fee');
      else failed.push(Object.values(r.problems)[0] ?? r.error);
    }
    setBusy(false);
    await client.invalidateQueries({ queryKey: ['fees-settings', partner.id] });
    const parts = [saved.length > 0 ? `Saved: ${saved.join(', ')}.` : '', ...failed].filter(Boolean);
    after(failed.length === 0, parts.join(' '));
  }

  const setLimit = (key: LimitKey, value: string) => limitForm && setLimitForm({ ...limitForm, [key]: value });

  return (
    <form onSubmit={(e) => void save(e)} className="qc-panel">
      <div className="qc-panel-title">
        <h3>Edit {partner.displayName}</h3>
        <span>Everything this partner is set to, in one place. Only what you change is saved.</span>
      </div>

      <div className="qc-profile-table">
        <Row label="Name">{partner.displayName}</Row>
        <Row label="Entity ID" htmlFor="entity-id">
          {sysadmin ? (
            <>
              <input
                id="entity-id"
                type="text"
                name="Code"
                inputMode="numeric"
                className="qc-profile-amount qc-profile-short"
                maxLength={4}
                autoComplete="off"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
              <small className="qc-field-hint"> Internal, four digits; no two partners share one.</small>
            </>
          ) : (
            <code>{partner.code}</code>
          )}
        </Row>
        <Row label="Partner type">
          {partner.parentProgramId ? `Location of ${partner.parentProgramName ?? 'a partner'}` : 'Partner'}
        </Row>
        <Row label="Processor">{partner.provider}</Row>
        {limits && <Row label="Currency">{limits.currency}</Row>}
        <Row label="Status">
          <span className={`qc-badge ${partner.isActive ? 'qc-badge-success' : 'qc-badge-muted'}`}>
            {partner.isActive ? 'Active' : 'Inactive'}
          </span>
          <small className="qc-field-hint"> Activate or deactivate it on Overview.</small>
        </Row>

        {partner.instantIssueAvailable && (
          <Row label="KYC">
            <fieldset className="qc-profile-choices" disabled={!sysadmin}>
              <legend className="qc-visually-hidden">KYC</legend>
              <label>
                <input type="radio" name="issuing" checked={!staffIssue} onChange={() => setStaffIssue(false)} />
                <span>
                  <strong>KYC required</strong>
                  <small className="qc-field-hint qc-profile-option-hint">
                    Cardholders are invited by e-mail, sign in and verify their identity; then their card is issued.
                  </small>
                </span>
              </label>
              <label>
                <input type="radio" name="issuing" checked={staffIssue} onChange={() => setStaffIssue(true)} />
                <span>
                  <strong>No KYC required</strong>
                  <small className="qc-field-hint qc-profile-option-hint">
                    Staff issue each card with its amount: no invitation, no identity check, no cardholder sign-in.
                  </small>
                </span>
              </label>
            </fieldset>
          </Row>
        )}

        {limitForm && (
          <>
            {LIMIT_ROWS.map(({ key, label, before, after: tail, optional }) => (
              <Row key={key} label={label} htmlFor={`limit-${key}`}>
                <span className="qc-profile-sentence">
                  {before}
                  <input
                    id={`limit-${key}`}
                    type="text"
                    inputMode="decimal"
                    name={key}
                    className="qc-profile-amount"
                    placeholder={optional ? 'No limit' : '0.00'}
                    required={!optional}
                    disabled={!sysadmin}
                    value={limitForm[key] ?? ''}
                    onChange={(e) => setLimit(key, e.target.value)}
                  />
                  {limitForm.currency} {tail}
                </span>
              </Row>
            ))}
            {limits?.isDefault && (
              <Row label="">
                <small className="qc-field-hint">
                  These are the platform defaults: this partner has no money limits of its own yet.
                </small>
              </Row>
            )}
          </>
        )}

        <Row label="Cardholder features">
          <div className="qc-profile-choices">
            {shown.map((f) => (
              <label key={f.feature}>
                <input
                  type="checkbox"
                  name={`feature_${f.feature}`}
                  disabled={!canFeatures}
                  checked={enabled[f.feature] === true}
                  onChange={(e) => setEnabled({ ...enabled, [f.feature]: e.target.checked })}
                />
                {featureLabel(f.feature)}
              </label>
            ))}
          </div>
        </Row>

        <Row label="Inactivity fee" htmlFor="fee-amount">
          <div className="qc-profile-choices">
            <span className="qc-profile-sentence">
              Fee amount
              <input
                id="fee-amount"
                type="text"
                inputMode="decimal"
                name={FEE_AMOUNT}
                aria-label="Inactivity fee amount"
                className="qc-profile-amount"
                placeholder="0.00"
                disabled={!sysadmin}
                value={fee.amount}
                onChange={(e) => setFee({ ...fee, amount: e.target.value })}
              />
              {limits?.currency ?? 'USD'} a month
            </span>
            <span className="qc-profile-sentence">
              Starts after
              <input
                type="text"
                inputMode="numeric"
                name={FEE_AFTER}
                aria-label="Months without activity before the fee"
                className="qc-profile-amount qc-profile-short"
                disabled={!sysadmin}
                value={fee.afterMonths}
                onChange={(e) => setFee({ ...fee, afterMonths: e.target.value })}
              />
              months without a purchase, refund, load or unload
            </span>
            <small className="qc-field-hint qc-profile-note">
              For reference only: QOR does not charge this fee. Send it to the processor, which has the bank approve it
              and then charges it itself.
            </small>
            {feeInherited && (
              <small className="qc-field-hint">
                Not set for this partner yet: what is shown is the platform default.
              </small>
            )}
            {feePending.length > 0 && (
              <small className="qc-field-hint qc-profile-pending">
                Waiting for approval (asked by {feePending[0]!.pending!.requestedBy}).
              </small>
            )}
          </div>
        </Row>
      </div>

      {problem && (
        <p className="qc-result-text" role="alert" style={{ marginTop: 12 }}>
          {problem}
        </p>
      )}
      <div className="qc-form-actions" style={{ marginTop: 16 }}>
        <span className="qc-field-hint">
          {sysadmin
            ? 'KYC, money limits and the fee: QOR system administrators only.'
            : canFeatures
              ? "You can change the cardholder features; the rest only QOR's system administrators change."
              : "Only QOR's administrators change this profile."}
        </span>
        {canFeatures && (
          <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Update this partner'}
          </button>
        )}
      </div>
    </form>
  );
}
